import { supabase } from "@/integrations/supabase/client";
import { getLocalSetting, setLocalSetting } from "./repos";

const GRACE_PERIOD_MS = 15 * 24 * 60 * 60 * 1000; // 15 dias em milissegundos

export interface LicenseInfo {
  status: "active" | "expired" | "blocked" | "inactive";
  key: string | null;
  expirationDate: string | null;
  lastValidation: string | null;
  daysRemaining: number;
}

const getLicenseModuleSettings = (data: any): { moduleCode: string; enabledModules: string } => {
  const moduleCode = data?.module_code || "access_control";
  const modules = Array.isArray(data?.enabled_modules) && data.enabled_modules.length > 0
    ? data.enabled_modules
    : [moduleCode];

  return {
    moduleCode,
    enabledModules: modules.join(",")
  };
};

// Obtém informações da licença gravada localmente
export const getLocalLicenseInfo = async (): Promise<LicenseInfo> => {
  const status = (await getLocalSetting("license_status")) || "inactive";
  const key = await getLocalSetting("license_key");
  const expirationDate = await getLocalSetting("license_expiration");
  const lastValidation = await getLocalSetting("last_online_validation");

  let daysRemaining = 0;
  if (expirationDate) {
    const diff = new Date(expirationDate).getTime() - Date.now();
    daysRemaining = Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)));
  }

  return {
    status: status as any,
    key,
    expirationDate,
    lastValidation,
    daysRemaining
  };
};

// Valida a licença online contra o Supabase central
export const validateLicenseOnline = async (
  licenseKey: string
): Promise<{ success: boolean; expiration?: string; error?: string }> => {
  try {
    if (!navigator.onLine) {
      return { success: false, error: "Sem conexão com a internet para validação." };
    }

    console.log("[License] Validando chave online...", licenseKey);

    // Consulta a tabela central de licenças no Supabase
    // Nota: Usamos consulta anônima. RLS deve permitir leitura de chaves ativas.
    let { data, error } = await (supabase as any)
      .from("licenses")
      .select("is_active, expiration_date, client_name, condominium_id, module_code, enabled_modules")
      .eq("license_key", licenseKey.trim())
      .maybeSingle();

    if (error && String(error.message || "").toLowerCase().includes("module_code")) {
      const fallback = await (supabase as any)
        .from("licenses")
        .select("is_active, expiration_date, client_name, condominium_id")
        .eq("license_key", licenseKey.trim())
        .maybeSingle();
      data = fallback.data;
      error = fallback.error;
    }

    if (error) {
      console.error("[License] Erro Supabase:", error);
      return { success: false, error: "Erro de comunicação com o servidor de licenças." };
    }

    if (!data) {
      return { success: false, error: "Chave de licença inválida ou inexistente." };
    }

    if (!data.is_active) {
      return { success: false, error: "Esta licença foi bloqueada pelo administrador." };
    }

    const expDateStr = data.expiration_date;
    if (expDateStr && new Date(expDateStr).getTime() < Date.now()) {
      return { success: false, error: "Esta licença expirou em " + new Date(expDateStr).toLocaleDateString() };
    }

    // Salva informações locais de ativação
    try {
      await setLocalSetting("license_key", licenseKey.trim());
      await setLocalSetting("license_status", "active");
      if (expDateStr) {
        await setLocalSetting("license_expiration", expDateStr);
      }
      await setLocalSetting("last_online_validation", new Date().toISOString());
      await setLocalSetting("client_name", data.client_name || "");
      const moduleSettings = getLicenseModuleSettings(data);
      await setLocalSetting("license_module", moduleSettings.moduleCode);
      await setLocalSetting("enabled_modules", moduleSettings.enabledModules);
      if (data.condominium_id) {
        await setLocalSetting("license_condominium_id", data.condominium_id);
      }
    } catch (storageError) {
      console.error("[License] Erro ao salvar licença local:", storageError);
      return {
        success: false,
        error: "Licença encontrada, mas o app não conseguiu salvar no banco local. Verifique a permissão sql:allow-execute do Tauri."
      };
    }

    return { success: true, expiration: expDateStr };
  } catch (err) {
    console.error("[License] Falha inesperada de validação:", err);
    return { success: false, error: "Falha na validação." };
  }
};

// Verifica a integridade da licença local (roda no startup e rotas)
export const checkLocalLicenseValidity = async (): Promise<boolean> => {
  try {
    const info = await getLocalLicenseInfo();

    if (info.status !== "active" || !info.key) {
      console.log("[License] Licença inativa.");
      return false;
    }

    // 1. Verifica se a licença expirou
    if (info.expirationDate && new Date(info.expirationDate).getTime() < Date.now()) {
      console.log("[License] Licença expirou.");
      await setLocalSetting("license_status", "expired");
      return false;
    }

    // 2. Se estiver online, tenta revalidar em segundo plano
    if (navigator.onLine) {
      validateLicenseOnline(info.key).then((res) => {
        if (!res.success) {
          console.warn("[License] Revalidação em segundo plano falhou:", res.error);
          if (res.error?.includes("bloqueada") || res.error?.includes("expirou")) {
            setLocalSetting("license_status", "blocked");
          }
        } else {
          console.log("[License] Revalidação automática online bem-sucedida.");
        }
      });
    } else {
      // 3. Se estiver offline, verifica o período de carência (limite de 15 dias sem internet)
      if (info.lastValidation) {
        const timeSinceLastValidation = Date.now() - new Date(info.lastValidation).getTime();
        if (timeSinceLastValidation > GRACE_PERIOD_MS) {
          console.warn("[License] Limite offline de 15 dias excedido. Requer revalidação online.");
          return false;
        }
      } else {
        // Se nunca validou online (inconsistência), bloqueia
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error("[License] Erro ao validar licença local:", error);
    return false;
  }
};
